/**
 * Feeds `xAxisTickLayout` with the live chart width so X tick labels rotate
 * only when horizontal ones would collide.
 *
 * The width arrives through Recharts' `ResponsiveContainer.onResize` (already
 * throttled by `debounce` on dashboard tiles). State is updated ONLY when the
 * derived layout actually changes, so dragging a tile's resize handle does not
 * re-render the chart on every frame — the identity bail-out is what keeps the
 * "no re-measure storm" tile contract intact.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { xAxisLabels, xAxisTickLayout, type XAxisTickLayout } from '../utils/axisFormat';

const sameLayout = (a: XAxisTickLayout, b: XAxisTickLayout) =>
  a.angle === b.angle && a.textAnchor === b.textAnchor && a.height === b.height;

export function useXAxisTickLayout(
  data: readonly Record<string, unknown>[],
  xDataKey: string,
): { layout: XAxisTickLayout; onResize: (width: number) => void } {
  const labels = useMemo(() => xAxisLabels(data, xDataKey), [data, xDataKey]);
  const widthRef = useRef(0);
  const labelsRef = useRef(labels);
  labelsRef.current = labels;

  const [layout, setLayout] = useState<XAxisTickLayout>(() => xAxisTickLayout(labels));
  const apply = useCallback((next: XAxisTickLayout) => {
    setLayout((prev) => (sameLayout(prev, next) ? prev : next));
  }, []);

  // Labels can change without a resize (a refetch returning new categories).
  useEffect(() => {
    apply(xAxisTickLayout(labels, widthRef.current));
  }, [apply, labels]);

  const onResize = useCallback(
    (width: number) => {
      widthRef.current = width;
      apply(xAxisTickLayout(labelsRef.current, width));
    },
    [apply],
  );

  return { layout, onResize };
}
